import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, ChevronDown, Building2, Bell, ShieldAlert, CreditCard, Activity, Menu } from "lucide-react";
import api from "../utils/api";

const CREDIT_ALERT_TYPES = ["credits_50_percent", "credits_90_percent", "credits_exhausted"];

const Navbar = ({ onToggleSidebar, sidebarCollapsed, activeAiAgentId, setActiveAiAgentId, aiAgents: passedAiAgents, agents, activeChatsCount }) => {
  const aiAgents = passedAiAgents || agents || [];
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [bellPulse, setBellPulse] = useState(false);
  const [creditAlerts, setCreditAlerts] = useState([]);
  // Tracks which credit thresholds have already fired a local alert this session.
  // Cleared when credits drop below the threshold (e.g. after a top-up).
  const shownThresholdsRef = useRef(new Set());

  // Smart Hide on Scroll Down / Slide Down on Scroll Up (guarded against flicker)
  const [isVisible, setIsVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const isVisibleRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const accumulatedDeltaRef = useRef(0);
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = (e) => {
      const target = e.target;
      if (!target) return;

      // Filter: only listen to scroll on the main scrollable container, document, or window
      const isMain = target.tagName === 'MAIN' || target === document || target === window;
      if (!isMain && !target.classList?.contains('overflow-y-auto')) return;

      // Ignore inner popovers, dropdowns, and modals
      if (
        target.closest?.('.agent-dropdown') ||
        target.closest?.('.notifications-dropdown') ||
        target.closest?.('[role="dialog"]') ||
        target.closest?.('.custom-scrollbar:not(main)')
      ) {
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = target.scrollTop !== undefined
            ? target.scrollTop
            : (window.pageYOffset || document.documentElement.scrollTop || 0);

          const scrollHeight = target.scrollHeight || document.documentElement.scrollHeight || 0;
          const clientHeight = target.clientHeight || window.innerHeight || 0;
          const scrollableHeight = scrollHeight - clientHeight;

          // If page has insufficient scroll room (< 120px), stay permanently visible
          if (scrollableHeight < 120) {
            if (!isVisibleRef.current) {
              setIsVisible(true);
              isVisibleRef.current = true;
            }
            lastScrollTopRef.current = scrollTop;
            accumulatedDeltaRef.current = 0;
            ticking = false;
            return;
          }

          // Always reveal at the top
          if (scrollTop <= 15) {
            if (!isVisibleRef.current) {
              setIsVisible(true);
              isVisibleRef.current = true;
            }
            setScrolled(false);
            lastScrollTopRef.current = scrollTop;
            accumulatedDeltaRef.current = 0;
            ticking = false;
            return;
          } else {
            setScrolled(true);
          }

          // Prevent feedback loop during active 320ms transition
          if (isTransitioningRef.current) {
            lastScrollTopRef.current = scrollTop;
            ticking = false;
            return;
          }

          // Avoid toggling near bottom edge clamp
          if (scrollTop >= scrollableHeight - 50 && isVisibleRef.current) {
            lastScrollTopRef.current = scrollTop;
            ticking = false;
            return;
          }

          const delta = scrollTop - lastScrollTopRef.current;

          // Reset accumulated delta if direction switches
          if ((delta > 0 && accumulatedDeltaRef.current < 0) || (delta < 0 && accumulatedDeltaRef.current > 0)) {
            accumulatedDeltaRef.current = 0;
          }
          accumulatedDeltaRef.current += delta;

          // Scroll down: deliberate continuous scroll > 10px past 35px scrollTop
          if (accumulatedDeltaRef.current > 10 && scrollTop > 35 && isVisibleRef.current) {
            setIsVisible(false);
            isVisibleRef.current = false;
            setIsOpen(false);
            setShowNotifications(false);
            accumulatedDeltaRef.current = 0;
            isTransitioningRef.current = true;
            clearTimeout(cooldownTimerRef.current);
            cooldownTimerRef.current = setTimeout(() => {
              isTransitioningRef.current = false;
            }, 360);
          }
          // Scroll up: deliberate continuous scroll < -10px
          else if (accumulatedDeltaRef.current < -10 && !isVisibleRef.current) {
            setIsVisible(true);
            isVisibleRef.current = true;
            accumulatedDeltaRef.current = 0;
            isTransitioningRef.current = true;
            clearTimeout(cooldownTimerRef.current);
            cooldownTimerRef.current = setTimeout(() => {
              isTransitioningRef.current = false;
            }, 360);
          }

          lastScrollTopRef.current = scrollTop;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll, { capture: true });
      clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  // Reset navbar visibility on agent or route switch
  useEffect(() => {
    setIsVisible(true);
    isVisibleRef.current = true;
    setScrolled(false);
    accumulatedDeltaRef.current = 0;
    isTransitioningRef.current = false;
    lastScrollTopRef.current = 0;
  }, [activeAiAgentId, location.pathname]);

  
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';

  const activeAiAgent = aiAgents?.find((p) => String(p.id) === String(activeAiAgentId));
  const userTenantId = user?.tenant_id 
    ? String(user.tenant_id) 
    : (activeAiAgent?.tenant_id 
        ? String(activeAiAgent.tenant_id) 
        : (aiAgents?.[0]?.tenant_id 
            ? String(aiAgents[0].tenant_id) 
            : (localStorage.getItem('activeTenantId') || '')
          )
      );


  // ── Live credit check (mirrors the old banner logic) ─────────────────────
  // Generates a local alert exactly ONCE per threshold crossing per session.
  // Uses shownThresholdsRef so repeated 60s polls don't re-trigger a dismissed alert.
  // Resets the flag only when credits drop back below the threshold (e.g. top-up).
  const fetchCreditsAndAlert = useCallback(async () => {
    if (!userTenantId) return;
    try {
      // Enterprise plans skip all credit threshold alerts — only token_spike is allowed
      try {
        const subRes = await api.request(`/api/subscription/${userTenantId}`);
        const planId = subRes?.data?.plan_id || subRes?.data?.planId || '';
        const planName = subRes?.data?.plan_name || subRes?.data?.planName || '';
        if (
          String(planId).toLowerCase() === 'enterprise' ||
          String(planName).toLowerCase() === 'enterprise'
        ) {
          return; // No credit alerts for enterprise plans
        }
      } catch (_subErr) {
        // Subscription check failed — fall through to normal alert logic
      }

      const res = await api.request(`/api/credits/${userTenantId}`);
      if (!res || !res.success || !res.data) return;
      const d = res.data;
      const monthly  = d.monthly_credits  || 0;
      const used     = d.credits_used     || 0;
      const topup    = d.topup_credits    || 0;
      const total    = d.total_credits    || 0;

      const monthlyPct = monthly > 0 ? (used / monthly) * 100 : 0;

      // ── Reset flags when credits drop BELOW a threshold (after top-up) ──
      if (monthlyPct < 50)  shownThresholdsRef.current.delete('50');
      if (monthlyPct < 90)  shownThresholdsRef.current.delete('90');
      if (total > 0)        shownThresholdsRef.current.delete('exhausted');

      const alerts = [];
      const now = new Date().toISOString();

      // Only push an alert if this threshold hasn't been shown yet this session
      if (total <= 0 && (monthly + topup) > 0 && !shownThresholdsRef.current.has('exhausted')) {
        shownThresholdsRef.current.add('exhausted');
        alerts.push({
          id: "__local_exhausted",
          alert_type: "credits_exhausted",
          type: "usage",
          title: "Credits Exhausted",
          message: `Your credits have been completely exhausted. Top up your credits to resume AI agent executions immediately.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      } else if (monthlyPct >= 90 && !shownThresholdsRef.current.has('90')) {
        shownThresholdsRef.current.add('90');
        alerts.push({
          id: "__local_90",
          alert_type: "credits_90_percent",
          type: "usage",
          title: "Credits Usage Alert — 90% Used",
          message: `You have used ${Math.round(monthlyPct)}% of your monthly credits. ${(monthly - used).toLocaleString()} credits remaining.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      } else if (monthlyPct >= 50 && !shownThresholdsRef.current.has('50')) {
        shownThresholdsRef.current.add('50');
        alerts.push({
          id: "__local_50",
          alert_type: "credits_50_percent",
          type: "usage",
          title: "Credits Usage Alert — 50% Used",
          message: `You have used ${Math.round(monthlyPct)}% of your monthly credits. ${(monthly - used).toLocaleString()} credits remaining.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      }

      if (alerts.length > 0) {
        setCreditAlerts(prev => {
          // Avoid duplicates: don't re-add an alert already in the list
          const existingIds = new Set(prev.map(a => a.id));
          const newAlerts = alerts.filter(a => !existingIds.has(a.id));
          return [...prev, ...newAlerts];
        });
        const autoOpened = sessionStorage.getItem("notifications_auto_opened") === "true";
        if (!autoOpened) {
          sessionStorage.setItem("notifications_auto_opened", "true");
          setShowNotifications(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch credits for notification:", err);
    }
  }, [userTenantId]);

  const fetchNotifications = useCallback(async () => {
    if (!userTenantId) return;
    try {
      const res = await api.get(`/notifications/${userTenantId}`);
      if (res && res.success) {
        const data = res.data || [];
        setNotifications(data);

        // Auto-open the panel once per session when there are unread DB credit alerts
        const hasUnreadCreditAlert = data.some(
          (n) => !n.is_read && CREDIT_ALERT_TYPES.includes(n.alert_type)
        );
        if (hasUnreadCreditAlert) {
          setBellPulse(true);
          const autoOpened = sessionStorage.getItem("notifications_auto_opened") === "true";
          if (!autoOpened) {
            sessionStorage.setItem("notifications_auto_opened", "true");
            setShowNotifications(true);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, [userTenantId]);

  useEffect(() => {
    fetchCreditsAndAlert();
    fetchNotifications();

    const handleCreditsUpdate = () => {
      fetchCreditsAndAlert();
      fetchNotifications();
    };

    window.addEventListener("credits_updated", handleCreditsUpdate);
    window.addEventListener("payment_success", handleCreditsUpdate);

    const interval = setInterval(fetchNotifications, 15000);
    const creditInterval = setInterval(fetchCreditsAndAlert, 60000); // refresh credit state every minute
    return () => {
      clearInterval(interval);
      clearInterval(creditInterval);
      window.removeEventListener("credits_updated", handleCreditsUpdate);
      window.removeEventListener("payment_success", handleCreditsUpdate);
    };
  }, [userTenantId, fetchCreditsAndAlert, fetchNotifications]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays}d ago`;
  };

  const markAsRead = useCallback(async (id) => {
    // Handle local (synthetic) credit alerts
    if (typeof id === "string" && id.startsWith("__local_")) {
      setCreditAlerts(prev => prev.filter(a => a.id !== id));
      const remaining = creditAlerts.filter(a => a.id !== id);
      if (remaining.length === 0) {
        setBellPulse(false);
        sessionStorage.removeItem("notifications_auto_opened");
      }
      return;
    }
    try {
      const res = await api.put(`/notifications/mark-read/${id}`);
      if (res && res.success) {
        setNotifications(prev => {
          const updated = prev.map(n => n.id === id ? { ...n, is_read: true } : n);
          // If no more unread credit alerts anywhere, stop pulsing and allow re-trigger
          const stillHasDbAlert = updated.some(
            n => !n.is_read && CREDIT_ALERT_TYPES.includes(n.alert_type)
          );
          if (!stillHasDbAlert && creditAlerts.length === 0) {
            setBellPulse(false);
            sessionStorage.removeItem("notifications_auto_opened");
          }
          return updated;
        });
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, [creditAlerts]);

  const markAllAsRead = useCallback(async () => {
    if (!userTenantId) return;
    try {
      const res = await api.put(`/notifications/mark-all-read/${userTenantId}`);
      if (res && res.success) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        // Also dismiss local credit alerts
        setCreditAlerts([]);
        // Reset pulse & allow re-trigger when new alerts arrive
        setBellPulse(false);
        sessionStorage.removeItem("notifications_auto_opened");
      }
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  }, [userTenantId]);

  // Filter DB notifications to last 7 days
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const dbNotifications = notifications.filter(n => {
    if (!n.created_at) return false;
    return new Date(n.created_at) >= sevenDaysAgo;
  });

  // Merge: local credit alerts at top, then DB notifications.
  // Deduplicate: if DB already has a notification of the same alert_type, skip the local one.
  const dbAlertTypes = new Set(dbNotifications.map(n => n.alert_type));
  const filteredLocalAlerts = creditAlerts.filter(a => !dbAlertTypes.has(a.alert_type));
  const recentNotifications = [...filteredLocalAlerts, ...dbNotifications];

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case "payment_failed":
      case "credits_exhausted":
      case "daily_spend_exceeded":
      case "token_spike":
        return <ShieldAlert size={16} className="text-red-500" />;
      case "subscription_renewing":
      case "plan_limit_reached":
      case "credits_50_percent":
      case "credits_90_percent":
        return <Activity size={16} className="text-amber-500" />;
      case "invoice_generated":
        return <CreditCard size={16} className="text-emerald-500" />;
      default:
        return <Bell size={16} className="text-gray-500" />;
    }
  };

  const getAlertBg = (alertType) => {
    switch (alertType) {
      case "payment_failed":
      case "credits_exhausted":
      case "daily_spend_exceeded":
      case "token_spike":
        return "bg-red-50";
      case "subscription_renewing":
      case "plan_limit_reached":
      case "credits_50_percent":
      case "credits_90_percent":
        return "bg-amber-50";
      case "invoice_generated":
        return "bg-emerald-50";
      default:
        return "bg-gray-50";
    }
  };

  useEffect(() => {
    if (userTenantId) {
      localStorage.setItem('activeTenantId', userTenantId);
    }
  }, [userTenantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".agent-dropdown")) {
        setIsOpen(false);
      }
      if (showNotifications && !event.target.closest(".notifications-dropdown")) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, showNotifications]);

  const assignedAiAgents = user?.assigned_ai_agents || [];

  const visibleAiAgents = isAdmin
    ? aiAgents
    : aiAgents.filter(p => assignedAiAgents.includes(p.id));



  return (
    <nav
      className={`sticky top-0 z-[80] w-full will-change-transform ${
        isVisible
          ? "translate-y-0 transition-transform duration-200 ease-out"
          : "-translate-y-full transition-transform duration-200 ease-in-out pointer-events-none"
      } ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-zinc-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
          : "bg-white border-b border-zinc-200/70"
      } px-3 sm:px-6 py-2 sm:py-2.5 flex items-center justify-between min-h-[52px] sm:min-h-[56px]`}
    >
      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
        {/* Mobile Sidebar Toggle Button */}
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
            aria-label="Toggle navigation menu"
          >
            <Menu size={17} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
        {isAdmin || (visibleAiAgents && visibleAiAgents.length > 1) ? (
          <div className="flex items-center gap-1.5 sm:gap-2 agent-dropdown relative">
            <span className="hidden lg:inline text-[10px] text-zinc-400 font-medium uppercase tracking-wider">
              Agent
            </span>
            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between gap-1.5 sm:gap-2 max-w-[150px] xs:max-w-[180px] sm:max-w-none sm:min-w-[170px] md:min-w-[190px] px-2 sm:px-3 py-1.5 bg-zinc-50/70 border border-zinc-200/80 rounded-lg hover:bg-zinc-100/70 hover:border-zinc-300 transition-all duration-150 group active:scale-[0.99] ${
                  isOpen ? "ring-2 ring-zinc-900/5 border-zinc-400 bg-white shadow-xs" : ""
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                  <div className="w-5 h-5 rounded-md bg-zinc-900 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <Building2 size={12} strokeWidth={2.2} />
                  </div>
                  <span className="text-xs font-medium text-zinc-800 truncate text-left">
                    {activeAiAgent?.name || "Select AI Agent"}
                  </span>
                </div>
                <ChevronDown
                  size={13}
                  className={`text-zinc-400 transition-transform duration-200 shrink-0 ${
                    isOpen ? "rotate-180 text-zinc-900" : "group-hover:text-zinc-600"
                  }`}
                />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-1.5 w-[calc(100vw-24px)] xs:w-64 sm:w-full sm:min-w-[200px] sm:max-w-[260px] bg-white border border-zinc-200 rounded-xl shadow-lg shadow-zinc-900/5 py-1 z-[9999] animate-in fade-in zoom-in-95 duration-100">
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider border-b border-zinc-100 mb-1 flex items-center justify-between">
                    <span>Your AI Agents</span>
                    <span className="text-zinc-500 font-medium text-[10px] bg-zinc-100 px-1.5 py-0.2 rounded">
                      {visibleAiAgents.length}
                    </span>
                  </div>
                  <div className="max-h-60 sm:max-h-64 overflow-y-auto custom-scrollbar p-1 space-y-0.5">
                    {visibleAiAgents.map((agent) => {
                      const isSelected = String(activeAiAgentId) === String(agent.id);
                      return (
                        <button
                          key={agent.id}
                          onClick={() => {
                            localStorage.setItem("activeAiAgentId", agent.id);
                            setActiveAiAgentId(agent.id);
                            setIsOpen(false);
                            window.location.reload();
                          }}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors group text-left ${
                            isSelected
                              ? "bg-zinc-100 text-zinc-900 font-medium"
                              : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                isSelected ? "bg-emerald-500" : "bg-zinc-300 group-hover:bg-zinc-400"
                              }`}
                            />
                            <span className="truncate">{agent.name}</span>
                          </div>
                          {isSelected && (
                            <span className="text-[10px] font-medium text-zinc-400">Active</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {isAdmin && (
                    <div className="border-t border-zinc-100 mt-1 pt-1 p-1">
                      <button
                        onClick={() => {
                          navigate("/ai-agents");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 rounded-lg transition-colors font-medium"
                      >
                        <Settings size={13} className="text-zinc-400 group-hover:text-zinc-600" />
                        Manage All AI Agents
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : !isAdmin && visibleAiAgents && visibleAiAgents.length === 1 ? (
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="hidden sm:inline text-[10px] text-zinc-400 font-medium uppercase tracking-wider">
              Agent
            </span>
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg max-w-[140px] xs:max-w-none">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0"></span>
              <span className="text-xs font-medium text-zinc-800 truncate">
                {visibleAiAgents[0].name}
              </span>
            </div>
          </div>
        ) : null}

        {/* Notification Bell Dropdown */}
        <div className="relative notifications-dropdown">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={`w-8 h-8 rounded-lg border border-zinc-200/80 bg-zinc-50/70 hover:bg-zinc-100/70 hover:border-zinc-300 text-zinc-600 hover:text-zinc-900 transition-all duration-150 relative flex items-center justify-center active:scale-95 shrink-0 ${
              showNotifications ? "ring-2 ring-zinc-900/5 border-zinc-400 bg-white shadow-xs" : ""
            }`}
            aria-label="View Notifications"
          >
            <Bell
              size={15}
              className={`${bellPulse ? "animate-pulse text-zinc-900" : ""}`}
            />
            {recentNotifications.some((n) => !n.is_read) && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-white"></span>
            )}
          </button>

          {showNotifications && (
            <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-auto sm:mt-1.5 sm:w-96 bg-white border border-zinc-200 rounded-xl shadow-xl shadow-zinc-900/10 py-2 z-50 animate-in fade-in zoom-in-95 duration-100 flex flex-col max-h-[calc(100vh-80px)] sm:max-h-[520px]">
              <div className="px-3.5 pb-2 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-zinc-900">Notifications</span>
                  {recentNotifications.filter((n) => !n.is_read).length > 0 && (
                    <span className="px-1.5 py-0.2 text-[10px] bg-zinc-900 text-white rounded font-medium">
                      {recentNotifications.filter((n) => !n.is_read).length} new
                    </span>
                  )}
                </div>
                {recentNotifications.some((n) => !n.is_read) && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors font-medium"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {/* Notification List */}
              <div className="overflow-y-auto max-h-[380px] flex-grow custom-scrollbar divide-y divide-zinc-50">
                {recentNotifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-zinc-400 text-xs flex flex-col items-center justify-center gap-2">
                    <Bell size={20} className="text-zinc-300" />
                    <span>No notifications</span>
                  </div>
                ) : (
                  recentNotifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => {
                        if (!n.is_read) {
                          markAsRead(n.id);
                        }
                        if (n.alert_type === "invoice_generated") {
                          navigate("/billing?tab=invoices");
                          setShowNotifications(false);
                        }
                      }}
                      className={`px-3.5 py-2.5 flex gap-2.5 transition-colors hover:bg-zinc-50 cursor-pointer ${
                        !n.is_read ? "bg-zinc-50/70" : ""
                      }`}
                    >
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${getAlertBg(
                          n.alert_type
                        )}`}
                      >
                        {getAlertIcon(n.alert_type)}
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <span
                            className={`text-xs truncate ${
                              !n.is_read ? "font-semibold text-zinc-900" : "font-medium text-zinc-700"
                            }`}
                          >
                            {n.title}
                          </span>
                          <span className="text-[10px] whitespace-nowrap text-zinc-400 shrink-0">
                            {formatTimeAgo(n.created_at)}
                          </span>
                        </div>
                        <p
                          className={`text-[11px] mt-0.5 leading-relaxed break-words line-clamp-2 ${
                            !n.is_read ? "text-zinc-700 font-normal" : "text-zinc-500"
                          }`}
                        >
                          {n.message}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sign Out button for non-admin human agents */}
        {!isAdmin && (
          <button
            onClick={() => {
              localStorage.removeItem("user");
              localStorage.removeItem("activeAiAgentId");
              localStorage.removeItem("activeTenantId");
              localStorage.removeItem("activeProduct");
              sessionStorage.removeItem("user");
              sessionStorage.clear();
              window.location.href = "/login";
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 hover:bg-rose-50 text-zinc-700 hover:text-rose-600 border border-zinc-200/70 hover:border-rose-200 rounded-lg transition-all font-medium text-xs shadow-2xs active:scale-95"
          >
            Sign Out
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
